import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Spinner from "../component/Spinner";
import ModalXemFile from "./ModalXemFile";

/**
 * Xem lại danh sách hợp đồng đã chọn và tải file .docx đã merge về máy.
 *
 * File nằm trên FTP sau endpoint download-image có yêu cầu auth, nên phải fetch kèm
 * Bearer token rồi tự tạo blob — không dùng thẻ <a download> trỏ thẳng URL được.
 */
export default function ModalXuatHopDong({ show, onHide, items = [], onRemove }) {
    const { user } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [errors, setErrors] = useState([]);
    // Hợp đồng đang mở xem file; null = không mở.
    const [viewing, setViewing] = useState(null);

    const hasFile = items.filter((i) => i.file_url);
    const noFile = items.filter((i) => !i.file_url);

    // Tên file an toàn: bỏ ký tự cấm trên Windows/macOS, tránh trùng bằng cách thêm id.
    const safeName = (item) => {
        const base = (item.title || "hop-dong-" + item.id).replace(/[\\/:*?"<>|]+/g, "-").trim();
        // Đuôi file nằm trong query (.../download-image?url=.../ten.docx), cắt ở '?' sẽ mất.
        const match = (item.file_url || "").match(/[?&]url=([^&]+)/i);
        const target = match ? decodeURIComponent(match[1]) : (item.file_url || "").split("?")[0];
        const ext = (target.split(".").pop() || "docx").toLowerCase();
        return base + "_" + item.id + "." + (ext && ext.length <= 5 ? ext : "docx");
    };

    const fetchFile = async (item) => {
        const res = await fetch(item.file_url, { headers: user.config.headers });
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.blob();
    };

    // 1 file => tải thẳng. Nhiều file => gộp zip cho gọn, khỏi bị trình duyệt chặn tải hàng loạt.
    const handleExport = async () => {
        if (hasFile.length === 0) return;
        setLoading(true);
        setErrors([]);
        setProgress({ current: 0, total: hasFile.length });

        const failed = [];
        try {
            if (hasFile.length === 1) {
                const item = hasFile[0];
                try {
                    const blob = await fetchFile(item);
                    saveAs(blob, safeName(item));
                } catch (err) {
                    failed.push({ id: item.id, title: item.title, message: err.message });
                }
                setProgress({ current: 1, total: 1 });
            } else {
                const zip = new JSZip();
                const used = {};
                for (let i = 0; i < hasFile.length; i++) {
                    const item = hasFile[i];
                    try {
                        const blob = await fetchFile(item);
                        // Chốt chặn trùng tên trong zip (JSZip ghi đè im lặng nếu trùng).
                        const base = safeName(item);
                        const seen = used[base] || 0;
                        used[base] = seen + 1;
                        const fname = seen === 0 ? base : base.replace(/(\.[^.]+)$/, "_" + seen + "$1");
                        zip.file(fname, blob);
                    } catch (err) {
                        failed.push({ id: item.id, title: item.title, message: err.message });
                    }
                    setProgress({ current: i + 1, total: hasFile.length });
                }
                const okCount = hasFile.length - failed.length;
                if (okCount > 0) {
                    const content = await zip.generateAsync({ type: "blob" });
                    saveAs(content, "hop-dong-" + okCount + "-file.zip");
                }
            }
        } catch (err) {
            failed.push({ id: "-", title: "Đóng gói file", message: err.message });
        }

        setErrors(failed);
        setLoading(false);
    };

    return (
        <>
        <Modal show={show} onHide={onHide} animation={true} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Hợp đồng đã chọn ({items.length})</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {items.length === 0 ? (
                    <div className="alert alert-warning mb-0">
                        Chưa chọn hợp đồng nào. Tích chọn ở danh sách rồi mở lại.
                    </div>
                ) : (
                    <>
                        {noFile.length > 0 && (
                            <div className="alert alert-warning py-1 px-2">
                                <small>
                                    <strong>{noFile.length}</strong> hợp đồng chưa có file đã merge, sẽ bị bỏ qua khi xuất.
                                </small>
                            </div>
                        )}
                        {errors.length > 0 && (
                            <div className="alert alert-danger py-1 px-2">
                                <small>
                                    <strong>{errors.length}</strong> file tải lỗi:{" "}
                                    {errors.map((e) => "#" + e.id).join(", ")}
                                </small>
                            </div>
                        )}
                        <table className="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th style={{ width: 50 }}>#</th>
                                    <th>Tiêu đề</th>
                                    <th>Đối tượng</th>
                                    <th style={{ width: 110 }}>File</th>
                                    <th style={{ width: 50 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, i) => (
                                    <tr key={item.id}>
                                        <td>{i + 1}</td>
                                        <td>{item.title}</td>
                                        <td>{item.fullname}</td>
                                        <td>
                                            {item.file_url ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => setViewing(item)}
                                                    title="Xem nội dung file đã merge"
                                                >
                                                    <span className="fas fa-eye"></span> Xem file
                                                </button>
                                            ) : (
                                                <span className="text-muted">Chưa có</span>
                                            )}
                                        </td>
                                        <td>
                                            {onRemove && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm p-0 bg-transparent text-danger"
                                                    title="Bỏ khỏi danh sách chọn"
                                                    onClick={() => onRemove(item.id)}
                                                >
                                                    <span className="fas fa-times"></span>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {loading && (
                            <div className="alert alert-info py-1 px-2 mb-0">
                                Đang tải file {progress.current}/{progress.total}...
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-sm btn-secondary" onClick={onHide} disabled={loading}>
                    Đóng
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={handleExport}
                    disabled={loading || hasFile.length === 0}
                >
                    {loading ? <Spinner /> : <>Xuất {hasFile.length > 1 ? hasFile.length + " file (.zip)" : "file"}</>}
                </button>
            </Modal.Footer>
        </Modal>

        <ModalXemFile
            show={!!viewing}
            onHide={() => setViewing(null)}
            url={viewing ? viewing.file_url : ""}
            title={viewing ? viewing.title : ""}
        />
        </>
    );
}
